/**
 * Coerce `?seller=` after urlHelpers.parse (which turns "1234" into a number
 * and "true"/"false" into booleans) back into a forum username string.
 * @param {*} value
 * @returns {string} trimmed username, or '' when the param is absent
 */
export const normalizeSellerParam = value => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return '';
};

/**
 * Map URL `seller=<forumUsername>` onto Sharetribe `author_id` after a resolve call.
 * `seller` must never be sent to listings.query.
 *
 * @param {Object} searchParams
 * @param {(username: string) => Promise<object|null>} resolveSeller
 * @returns {Promise<{ searchParams: Object, activeSeller: object|null, sellerNotFound: boolean, skipQuery: boolean }>}
 */
export const resolveSellerSearchParams = async (searchParams = {}, resolveSeller) => {
  const { seller: rawSeller, ...rest } = searchParams;
  const username = normalizeSellerParam(rawSeller);

  if (!username) {
    return {
      searchParams: rest,
      activeSeller: null,
      sellerNotFound: false,
      skipQuery: false,
    };
  }

  const resolver = typeof resolveSeller === 'function' ? resolveSeller : async () => null;
  const activeSeller = await resolver(username);
  const id = activeSeller?.id;
  if (!id) {
    return {
      searchParams: rest,
      activeSeller: null,
      sellerNotFound: true,
      skipQuery: true,
    };
  }

  return {
    searchParams: { ...rest, author_id: id },
    activeSeller,
    sellerNotFound: false,
    skipQuery: false,
  };
};

export const emptySellerSearchResponse = (perPage = 24) => ({
  data: {
    data: [],
    meta: {
      totalItems: 0,
      totalPages: 0,
      page: 1,
      perPage,
    },
  },
});
